import {
  DateRangeAlias,
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { ISearchTypeaheadActionSheetButton } from '@instagram-account/InstagramAccountSearchTypeaheadActionSheet';
import {
  AutocompleteGroup,
  IInstagramSourceAndCountData,
  IInstagramSourceAndCountWithAutocomplete,
} from '@instagram-account/models/IInstagramAutocompleteResponses';
import {
  IInstagramMultiSearchRequestBody,
  IInstagramSearchRequestBody,
} from '@instagram-account/models/IInstagramSearchRequestBody';
import InstagramHashtag from '@models/InstagramHashtag';
import InstagramUsername from '@models/InstagramUsername';
import TimeZoneId from '@models/TimeZoneId';

interface IGenerateDownloadFilename {
  name: string;
  type: string;
  dateRange: IAliasedDateRange;
  extension?: string;
}

export function generateDownloadFilename({
  name,
  type,
  dateRange,
  extension = 'csv',
}: IGenerateDownloadFilename): string {
  const endDate =
    dateRange.startDate !== dateRange.endDate ? `_${dateRange.endDate}` : '';
  const timeStr = `${dateRange.startDate}${endDate}`;

  return `instagram_${type}_${name}_${timeStr}.${extension}`;
}

export function getCustomDateRangePickerAliases(
  timeZone: TimeZoneId,
  aliases: DateRangeAlias[] = ['last7Days', 'last30Days', 'last90Days'],
): IAliasedDateRange[] {
  return aliases.map((alias) => getAliasedDateRange(alias, timeZone));
}

/**
 * Gets the optional search prefix for the header search bar
 * The prefix can be `#` or `@` which limits the search to only hashtags or usernames
 */
export function getOptionalInstagramPrefix(val: string): string {
  return val.startsWith('#') || val.startsWith('@') ? val.substring(0, 1) : '';
}

interface ICreateSearchTypeaheadActionSheetButtonProps {
  autocompleteResults: IInstagramSourceAndCountWithAutocomplete[];
  filterParam: string;
  onClick: (result: IInstagramSourceAndCountWithAutocomplete) => void;
}

export function createSearchTypeaheadActionSheetButtonProps({
  autocompleteResults,
  filterParam,
  onClick,
}: ICreateSearchTypeaheadActionSheetButtonProps): ISearchTypeaheadActionSheetButton[] {
  return autocompleteResults
    .filter((result) => result.source.startsWith(filterParam))
    .map((result) => ({
      header: result.group,
      label: result.source,
      count: `${result.count} Posts`,
      onClick: () => onClick(result),
    }));
}

export function transformAutocompleteData(
  data: IInstagramSourceAndCountData,
): IInstagramSourceAndCountWithAutocomplete[] {
  const hashtags: IInstagramSourceAndCountWithAutocomplete[] =
    data.hashtags.map((hashtag) => ({
      ...hashtag,
      source: hashtag.source,
      group: AutocompleteGroup.HASHTAG,
    }));

  const usernames: IInstagramSourceAndCountWithAutocomplete[] =
    data.usernames.map((username) => ({
      ...username,
      source: username.source,
      group: AutocompleteGroup.USERNAMES,
    }));

  return [...hashtags, ...usernames];
}

export function generateInstagramSearchRequestBody(
  searchTerms: IInstagramSourceAndCountWithAutocomplete[],
): IInstagramSearchRequestBody {
  return searchTerms.reduce(
    (acc: IInstagramSearchRequestBody, term) => {
      if (term.group === AutocompleteGroup.HASHTAG) {
        acc.searchedHashtags.push(InstagramHashtag(term.source));
      }

      if (term.group === AutocompleteGroup.USERNAMES) {
        acc.searchedUsernames.push(InstagramUsername(term.source));
      }

      return acc;
    },
    {
      searchedHashtags: [],
      searchedUsernames: [],
    },
  );
}
export function generateMultiHandleInstagramSearchRequestBody(
  searchTerms: IInstagramSourceAndCountWithAutocomplete[],
  mentionedName: string[],
): IInstagramMultiSearchRequestBody {
  return searchTerms.reduce(
    (acc: IInstagramMultiSearchRequestBody, term) => {
      if (term.group === AutocompleteGroup.HASHTAG) {
        acc.searchedHashtags.push(InstagramHashtag(term.source));
      }
      if (term.group === AutocompleteGroup.USERNAMES) {
        acc.searchedUsernames.push(InstagramUsername(term.source));
      }
      return acc;
    },
    {
      searchedHashtags: [],
      searchedUsernames: [],
      mentionedUsernames: mentionedName,
    },
  );
}
